<template>
  <div class="contenedor-principal">
    <h1>Estramonio (Datura stramonium) en Azkorri: Riesgos y Precauciones</h1>
    <h2>Una planta tóxica en la costa de Bizkaia: Cómo identificarla y evitar peligros</h2>

    <h3>¿Qué es el Estramonio?</h3>
    <p>El <strong>estramonio</strong> (<em>Datura stramonium</em>), popularmente conocido como "Higuera del infierno" o
      "Trompeta de los ángeles", es una planta perteneciente a la familia de las solanáceas. Es mundialmente conocida
      por sus propiedades altamente tóxicas. Aunque algunas de sus partes han sido utilizadas tradicionalmente con fines
      medicinales o rituales, su consumo o simple manipulación puede resultar extremadamente peligroso.</p>

    <p>Es una planta oportunista que suele proliferar de forma natural en terrenos removidos, escombreras, o zonas con
      mucha materia orgánica (como bordes de caminos o cerca de campos abonados).</p>

    <div class="alerta-importante">
      <p><strong>⚠️ ¡Atención en Azkorri!</strong><br>
        Desde el equipo de Geobizi hemos detectado la presencia de varios ejemplares en la zona de Azkorri (Getxo), muy
        cerca de senderos accesibles y frecuentados por familias, niños y perros. Es fundamental aprender a reconocerla
        para evitar el contacto directo: <strong>no la toques, no arranques sus flores o frutos y mantén a tus mascotas
          siempre alejadas de ella.</strong></p>
    </div>

    <h3>Origen y Expansión</h3>
    <p>El origen exacto del estramonio es motivo de debate científico, aunque la evidencia actual sugiere que el género
      <em>Datura</em> tiene su cuna evolutiva en América Central y las Grandes Antillas. Desde allí, se habría expandido
      de forma natural por toda América del Norte y del Sur.</p>
    <p>Hoy en día, el estramonio es una planta cosmopolita. Crece en zonas cálidas y templadas de casi todo el planeta
      (excepto en climas polares). Su amplia distribución se explica por el intenso comercio de semillas en el pasado y
      su asombrosa capacidad para naturalizarse en nuevos entornos europeos, como nuestra costa vasca.</p>

    <h3>Riesgos y Síntomas de Intoxicación</h3>
    <p>Toda la planta (hojas, tallo, flores y especialmente las semillas) contiene niveles altos de alcaloides
      tropánicos, como la <strong>atropina y la escopolamina</strong>. La intoxicación puede producirse por ingestión
      accidental o por contacto prolongado con la savia.</p>

    <p>Los síntomas de intoxicación por estramonio incluyen:</p>
    <ul>
      <li>Sequedad extrema de boca y piel.</li>
      <li>Dilatación de las pupilas (midriasis) y visión borrosa.</li>
      <li>Desorientación, agitación y confusión mental.</li>
      <li>Taquicardia y aumento de la temperatura corporal.</li>
      <li>Alucinaciones intensas y delirios.</li>
      <li>En casos muy graves: coma o paradas cardiorrespiratorias.</li>
    </ul>

    <h3>Primeros Auxilios: ¿Qué hacer ante una exposición?</h3>
    <p>Si sospechas que tú, un familiar o tu mascota ha entrado en contacto o ha ingerido alguna parte de esta planta,
      actúa con rapidez:</p>
    <ul>
      <li><strong>No induzcas el vómito</strong> salvo que te lo indique expresamente un profesional sanitario.</li>
      <li>Lava abundantemente con agua y jabón las zonas de la piel que hayan estado en contacto con la planta.</li>
      <li><strong>Acude inmediatamente a Urgencias (Llama al 112).</strong> Si es posible, aporta información o una foto
        de la planta para facilitar el diagnóstico médico.</li>
      <li>En el caso de animales, contacta de urgencia con un centro veterinario si notas letargo, temblores o
        salivación excesiva.</li>
    </ul>

    <h3>Control y Prevención en Entornos Naturales</h3>
    <p>La gestión y erradicación de ejemplares de estramonio en zonas públicas debe dejarse siempre en manos de personal
      cualificado del ayuntamiento o servicios forestales. En espacios naturales, la mejor herramienta es la educación:
      señalizar las zonas de riesgo e informar a la población local sobre cómo convivir de forma segura con la flora de
      nuestro entorno.</p>

    <p>Recuerda: si ves estramonio paseando por Azkorri o cualquier otra área recreativa, <strong>no intentes arrancarlo
        tú mismo</strong>. Avisa a las autoridades municipales o a los responsables de medioambiente del lugar.</p>

    <div class="caja-recurso">
      <h3>Aprende a conocer tu entorno con Geobizi</h3>
      <p>
        Saber identificar las plantas de nuestros caminos es vital para pasear seguros por la naturaleza. En Geobizi
        organizamos <strong>talleres de medioambiente y rutas interpretativas</strong> donde enseñamos a familias y
        adultos a distinguir la flora autóctona de las especies invasoras o peligrosas.
      </p>
      <div class="botones-recurso">
        <router-link to="/calendario" class="proximas-actividades">Ver próximas actividades</router-link>
        <router-link to="/contacto" class="btn-ver-mas">Contactar con nosotros</router-link>
      </div>
    </div>

    <div class="imagen-trio">
      <div>
        <img class="img" src="/imagenes/blog/detalle/estramonio1.avif"
          alt="Flor blanca en forma de trompeta y fruto espinoso del estramonio"
          title="Datura stramonium — flor y fruto" loading="lazy" />
      </div>
      <div>
        <img class="img" src="/imagenes/blog/detalle/estramonio2.avif"
          alt="Planta tóxica de estramonio creciendo cerca de un sendero accesible en Bizkaia"
          title="Estramonio en zona accesible" loading="lazy" />
      </div>
      <div>
        <img class="img" src="/imagenes/blog/detalle/estramonio3.avif"
          alt="Detalle del fruto en forma de cápsula cubierta de espinas del estramonio"
          title="Fruto espinoso del estramonio" loading="lazy" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

// URL actualizada según el router
const pageUrl = 'https://www.geobizi.com/blog/estramonio'
const ogImage = 'https://www.geobizi.com/imagenes/blog/detalle/estramonio1.avif'

useHead({
  // Título SEO optimizado
  title: 'Estramonio en Azkorri: Identificación y Peligros | Geobizi',
  meta: [
    // Descripción ampliada
    { name: 'description', content: '¿Has visto una planta con trompetas blancas y frutos espinosos en Azkorri? Es el Estramonio. Conoce sus riesgos, síntomas de intoxicación y qué hacer.' },
    { name: 'robots', content: 'index, follow' },
    { name: 'author', content: 'Geobizi' },
    { name: 'language', content: 'es' },
    { property: 'og:title', content: 'Alerta: Estramonio en Azkorri — Identificación y Precauciones | Geobizi' },
    { property: 'og:description', content: 'Descubre los peligros del Estramonio (Datura stramonium) en la costa de Bizkaia. Aprende a identificarla y mantén seguros a niños y mascotas.' },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: pageUrl },
    { property: 'og:image', content: ogImage },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: ogImage }
  ],
  link: [
    { rel: 'canonical', href: pageUrl },
    { rel: 'image_src', href: ogImage }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "Estramonio (Datura stramonium) en Azkorri: riesgos y precauciones",
        "description": "Información sobre el estramonio encontrado en Azkorri, identificando sus síntomas de toxicidad y ofreciendo pautas de primeros auxilios y prevención.",
        "image": ogImage,
        "author": {
          "@type": "Organization",
          "name": "Geobizi",
          "url": "https://www.geobizi.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Geobizi",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.geobizi.com/imagenes/GeobiziLogo.png"
          }
        },
        // Añadidas fechas para validez del Schema Article
        "datePublished": "2025-05-10T08:00:00+01:00",
        "dateModified": "2026-04-14T10:00:00+02:00",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": pageUrl
        }
      })
    }
  ]
})
</script>

<style scoped>
/* Estilos para alertas visuales */
.alerta-importante {
  background-color: var(--yellow);
  border-left: 5px solid var(--orange);
  padding: 1rem;
  margin: 1.5rem 0;
  border-radius: 4px;
}

.alerta-importante p {
  color: #856404;
  margin: 0;
}

/* Estilos de la caja de enlazado interno (reutilizados del Flysch) */
.caja-recurso {
  background-color: var(--megashoftgreen);
  border-left: 4px solid var(--shoftgreen);
  padding: 1.5rem;
  margin: 2.5rem 0;
  border-radius: 0 0.5rem 0.5rem 0;
}

.caja-recurso h3 {
  margin-top: 0;
  color: var(--lightgreen);
  font-size: 1.2rem;
}

.caja-recurso p {
  margin-bottom: 1.5rem;
  color: var(--darkgrey);
}

.botones-recurso {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.proximas-actividades {
  background-color: var(--supershoftgreen);
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.proximas-actividades:hover {
  background-color: var(--shoftgreen);
  color: var(--white);
}

.btn-ver-mas {
  background-color: transparent;
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border: 1px solid var(--shoftgreen);
  border-radius: 4px;
  transition: all 0.3s;
}

.btn-ver-mas:hover {
  background-color: var(--shoftgreen);
  color: white;
}

/* Este apartado es para las listas con hojitas */

/* Quitar los puntos por defecto de la lista */
.contenedor-principal ul {
  list-style-type: none;
  padding-left: 0;
}

/* Dar espacio a la izquierda para que quepa el icono */
.contenedor-principal li {
  position: relative;
  padding-left: 2rem;
  /* Espacio para las hojitas */
  margin-bottom: 0.8rem;
  line-height: 1.5;
}

/* Insertar la imagen de las hojitas */
.contenedor-principal li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  width: 1.2rem;
  height: 1.2rem;

  /* AQUÍ ESTÁ EL CAMBIO MÁGICO: subimos dos carpetas (../../) */
  background-image: url('../../assets/Hojitas.png');

  background-size: contain;
  background-repeat: no-repeat;
}
</style>